import React, { useEffect, useState } from "react";
import { PackageCheck, Plus, Trash2 } from "lucide-react";
import api from "../lib/api";
import { Card, Button, PageHeader, PageLoader } from "../lib/ui";
import { useToast } from "../lib/toast";
import { useT } from "../lib/i18n";
import { compressImage } from "../lib/image";
import { PhotoEvidence } from "../components/PhotoEvidence";

// Beverage Receiving — beverage-only direct stock-in. Reuses the branch movement ledger.
// Kept fully separate from the (frozen) Kitchen order-based receiving flow.
export default function BeverageReceiving() {
  const { t } = useT();
  const toast = useToast();
  const [products, setProducts] = useState([]);
  const [lines, setLines] = useState([{ product_id: "", quantity: "" }]);
  const [supplier, setSupplier] = useState("");
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadHistory = () => api.get("/beverage/receiving").then(({ data }) => setHistory(data)).catch(() => setHistory([]));

  useEffect(() => {
    api.get("/branch/my-inventory")
      .then(({ data }) => setProducts(data.filter((r) => (r.product_type || "") !== "PREPARED")))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
    loadHistory();
  }, []);

  const setLine = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const addLine = () => setLines([...lines, { product_id: "", quantity: "" }]);
  const removeLine = (i) => setLines(lines.filter((_, j) => j !== i));

  const submit = async () => {
    const valid = lines.filter((l) => l.product_id && Number(l.quantity) > 0)
      .map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity) }));
    if (valid.length === 0) { toast.error(t("Add at least one beverage item with quantity")); return; }
    setSaving(true);
    try {
      await api.post("/beverage/receiving", { lines: valid, supplier, notes, photos });
      toast.success(t("Beverage stock received"));
      setLines([{ product_id: "", quantity: "" }]); setSupplier(""); setNotes(""); setPhotos([]);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Failed to save"));
    } finally { setSaving(false); }
  };

  if (loading) return <PageLoader />;

  return (
    <div className="animate-fade-up pb-24 lg:pb-2" data-testid="beverage-receiving">
      <PageHeader icon={PackageCheck} title={t("Beverage Receiving")}
        subtitle={t("Receive incoming beverage ingredients into branch stock")} />

      <Card className="mt-4 space-y-4 p-5" data-testid="receiving-form">
        <div className="space-y-2">
          {lines.map((l, i) => (
            <div key={i} className="flex items-end gap-2" data-testid={`receiving-line-${i}`}>
              <div className="flex-1">
                <label className="mb-1 block text-xs font-semibold text-ink-soft">{t("Beverage Ingredient")}</label>
                <select value={l.product_id} onChange={(e) => setLine(i, "product_id", e.target.value)}
                  data-testid={`receiving-product-${i}`}
                  className="w-full rounded-xl border border-line bg-app px-3 py-2 text-sm">
                  <option value="">{t("Select ingredient")}</option>
                  {products.map((p) => (
                    <option key={p.product_id} value={p.product_id}>{p.product_name} ({p.unit})</option>
                  ))}
                </select>
              </div>
              <div className="w-28">
                <label className="mb-1 block text-xs font-semibold text-ink-soft">{t("Qty")}</label>
                <input type="number" min="0" step="0.01" value={l.quantity}
                  onChange={(e) => setLine(i, "quantity", e.target.value)}
                  data-testid={`receiving-qty-${i}`}
                  className="w-full rounded-xl border border-line bg-app px-3 py-2 text-sm" placeholder="0" />
              </div>
              {lines.length > 1 && (
                <button type="button" onClick={() => removeLine(i)} data-testid={`receiving-remove-${i}`}
                  className="mb-1 rounded-lg p-2 text-rose-500 hover:bg-rose-50"><Trash2 size={16} /></button>
              )}
            </div>
          ))}
          <Button variant="ghost" onClick={addLine} data-testid="receiving-add-line" className="text-xs">
            <Plus size={14} /> {t("Add Item")}
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-semibold text-ink-soft">{t("Supplier")} ({t("optional")})</label>
            <input value={supplier} onChange={(e) => setSupplier(e.target.value)} data-testid="receiving-supplier"
              className="w-full rounded-xl border border-line bg-app px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-ink-soft">{t("Notes")} ({t("optional")})</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="receiving-notes"
              className="w-full rounded-xl border border-line bg-app px-3 py-2 text-sm" />
          </div>
        </div>

        <PhotoEvidence
          tokens={photos} setTokens={setPhotos}
          uploadFile={(file) => compressImage(file, 1024, 0.7)}
          max={4} testid="receiving-photos"
          label={t("Delivery photo (optional)")} />

        <Button onClick={submit} loading={saving} data-testid="receiving-submit" className="w-full sm:w-auto">
          {t("Receive Stock")}
        </Button>
      </Card>

      <h3 className="mt-8 mb-2 text-sm font-bold text-ink">{t("Recent Receipts")}</h3>
      <div className="space-y-2" data-testid="receiving-history">
        {history.length === 0 && <p className="text-sm text-ink-mute">{t("No receipts yet.")}</p>}
        {history.map((r) => (
          <Card key={r.id} className="p-4" data-testid={`receiving-receipt-${r.id}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-ink">{r.receipt_no}</span>
              <span className="text-[11px] text-ink-mute">{(r.created_at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {(r.lines || []).map((ln, i) => (
                <span key={i} className="rounded-lg bg-app px-2 py-1 text-[11px] text-ink-soft">
                  {ln.product_name}: <b>{ln.quantity} {ln.unit}</b>
                </span>
              ))}
            </div>
            {r.supplier && <p className="mt-1 text-[11px] text-ink-mute">{t("Supplier")}: {r.supplier}</p>}
          </Card>
        ))}
      </div>
    </div>
  );
}
